/**
 * Handing a file to the browser — the one impure step, in one place.
 *
 * `csv.ts` had this inline and was the only caller until S-B38 added the
 * download publish target, whose whole product promise is a file. Lifted here
 * rather than copied for the reason that file states about its own split: the
 * `Blob`/`createObjectURL` dance is what jsdom does not implement the way a
 * browser does, so it is the part that has to be isolated behind a seam a test
 * can stand in for. Everything above it stays pure and testable.
 *
 * **The object URL is revoked immediately after the click** rather than left for
 * the page's lifetime — the anchor has already been followed by then, and an
 * unrevoked blob holds the whole file in memory until the tab closes.
 */
export function saveFile(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

/**
 * The same, for text the API returned as a string.
 *
 * `type` carries the charset the API named — `text/markdown; charset=utf-8` —
 * and it is not decoration: the articles this product writes carry Telugu and
 * Devanagari, and a file saved without it is opened as the local code page by
 * more than one editor on Windows.
 */
export function saveTextFile(filename: string, text: string, type: string): void {
  saveFile(filename, new Blob([text], { type }))
}
